'use client';

import {
  DetailedEpisodeType,
  PodcastType,
} from '@elsevier-health/osmosis-shared/types/podcast';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { H1 } from '@osmosis/tailwind-ui/components/Text';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { P } from '@osmosis/tailwind-ui/components/Text';
import Link from 'next/link';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PublishedDate from './PublishedDate';
import PodcastSubscribe from './PodcastSubscribe';
import { faFileDownload } from '@fortawesome/pro-duotone-svg-icons/faFileDownload';
import { SIMPLECAST_PLAYER_URL } from '../../constants/urls';

type Props = {
  podcast: PodcastType;
  episode: DetailedEpisodeType;
};

/**
 * Episode page for podcast.
 */
const EpisodePage = ({ podcast, episode }: Props) => {
  const intl = useIntl();

  return (
    <Box cn="sm:p-5 p-8 max-w-[800px] m-auto">
      <Box cn="mb-8">
        <Link href="/podcasts" className="opacity-50">
          <FormattedMessage
            id="episode-page.episodes_breadcrumbs"
            defaultMessage="Episodes > Episode {number}"
            values={{ number: episode.number }}
          />
        </Link>
      </Box>

      <Box cn="flex flex-col gap-8">
        <Box cn="flex flex-col gap-8">
          <P cn="text-lg uppercase opacity-75">
            <FormattedMessage
              id="podcast-episode-row.episode_number"
              defaultMessage="Episode {number}"
              values={{ number: episode.number }}
            />
          </P>

          <H1>{episode.title}</H1>
          <PublishedDate date={episode.published_at} />
          <P>{episode.description}</P>

          <iframe
            width="100%"
            seamless
            title={intl.formatMessage({
              id: 'episode-page.iframe',
              defaultMessage: 'Simplecast Player',
            })}
            src={`${SIMPLECAST_PLAYER_URL}/${episode.id}`}
          />
        </Box>

        <Box cn="flex gap-8 justify-between items-end">
          <PodcastSubscribe podcast={podcast} />

          <Box>
            <a
              href={episode.audio_file_url}
              download={true}
              target="_blank"
              data-testid="download-btn"
            >
              <Button
                data-testid="btn"
                variant="outline"
                startIcon={<Icon icon={faFileDownload} />}
              >
                <FormattedMessage
                  id="podcast-page.download-btn"
                  defaultMessage="Download"
                />
              </Button>
            </a>
          </Box>
        </Box>

        <Box cn="gap-8 flex flex-col">
          <H2>
            <FormattedMessage
              id="common.transcript"
              defaultMessage="Transcript"
            />
          </H2>

          <Box
            cn="opacity-75"
            dangerouslySetInnerHTML={{ __html: episode.transcription }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EpisodePage;
